import { apiGet, apiPost, apiPatch, apiDelete } from './api-client';
import type { Message, ChatSession, CoachingCategory } from '@/types';

/**
 * Create a new chat session
 */
export async function createSession(): Promise<ChatSession> {
  return apiPost<ChatSession>('/api/sessions');
}

/**
 * Get all sessions for current user that have at least one message
 */
export async function getSessions(): Promise<ChatSession[]> {
  return apiGet<ChatSession[]>('/api/sessions');
}

/**
 * Get sessions filtered by category (only sessions with messages)
 */
export async function getSessionsByCategory(
  category: CoachingCategory | null
): Promise<ChatSession[]> {
  if (category === null) {
    return getSessions();
  }
  return apiGet<ChatSession[]>(`/api/sessions?category=${category}`);
}

/**
 * Delete a chat session
 */
export async function deleteSession(sessionId: string): Promise<void> {
  await apiDelete(`/api/sessions/${sessionId}`);
}

/**
 * Update session category
 */
export async function updateSessionCategory(
  sessionId: string,
  category: CoachingCategory
): Promise<void> {
  await apiPatch(`/api/sessions/${sessionId}/category`, { category });
}

/**
 * Get messages for a session
 */
export async function getMessages(sessionId: string): Promise<Message[]> {
  return apiGet<Message[]>(`/api/sessions/${sessionId}/messages`);
}

/**
 * Send a message and get AI response
 */
export async function sendMessage(
  sessionId: string,
  content: string
): Promise<Message> {
  const data = await apiPost<{ message: Message }>('/api/chat', { sessionId, content });
  return data.message;
}

/**
 * Create a new session AND send the first message atomically.
 * This is used for lazy session creation - sessions are only
 * persisted to DB when the first message is sent.
 */
export async function createSessionWithMessage(
  content: string,
  options?: { category?: CoachingCategory; agentId?: string }
): Promise<{ session: ChatSession; message: Message }> {
  return apiPost<{ session: ChatSession; message: Message }>('/api/sessions-with-message', {
    content,
    category: options?.category,
    agentId: options?.agentId,
  });
}

/**
 * Subscribe to new messages in a session (no-op: realtime via SSE not yet wired)
 */
export function subscribeToMessages(
  _sessionId: string,
  _onMessage: (message: Message) => void
) {
  return () => {};
}

/**
 * Subscribe to session updates (no-op: realtime via SSE not yet wired)
 */
export function subscribeToSessions(
  _userId: string,
  _onSessionChange: (session: ChatSession, eventType: 'INSERT' | 'UPDATE' | 'DELETE') => void
) {
  return () => {};
}
